import { AgmMap } from '@agm/core';
import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ChangeDetectorRef } from '@angular/core';
import { sweetAlert } from 'src/app/utils/swal';
import { AssigTripsService } from '../../component/assigned-trips/assigntrips-service/assigntrips-service';
import { legTypeMap, timeCalculation } from '../../utils/utils.common';
import { KpisService } from '../../component/kpis/kpis-service/kpis.service';
import { takeUntil } from 'rxjs/operators';
import { componentDestroyed } from '@w11k/ngx-componentdestroyed';
import { DriversService } from '../../component/drivers/drivers-service/drivers-service';
import { environment } from 'src/environments/environment';
import { ServiceAreasAddComponent } from '../../component/service-areas/service-areas-add/service-areas-add.component';
import { ServiceAreasService } from '../../component/service-areas/service-areas-service/service-areas.service';
import { LevelOfServiceService } from '../../component/level-of-service/service/level-of-service.service';
import { CorporateService } from '../../component/corporate-user/corporate-service/corporate-service';
import { SharedDataService } from 'src/app/service/shared-service/shared-data.service';
import { FleetService } from 'src/app/component/fleet/fleet-service/fleet.service';
import { FarmoutService } from 'src/app/service/farmout-service/farmout.service';

@Component({
  selector: 'app-trip-detail-model',
  templateUrl: './trip-detail-model.component.html',
  styleUrls: ['./trip-detail-model.component.css']
})
export class TripDetailModelComponent implements OnInit, OnDestroy {
  legTypeMap: any = legTypeMap;
  @Input('tripDetailModalObj') trip: any;
  @Input() source: any;
  @Input() corporatesByKey: any
  @ViewChild("AgmMap", { static: false }) agmElement: AgmMap;
  @Output() onCloseModal = new EventEmitter<string>();
  agmMap;
  bounds;
  latitude: number;
  longitude: number;
  zoom: number = 12;
  eta;
  contact;
  tripData: any;
  driverDetails;
  preferredDriverDetails;

  trackinURL;
  serviceAreas: any;
  user;
  isFarmoutIntegration = false
  assistList;
  driverList:any;
  selectedStatus = 'active';
  preDrivers = {};
  isTaxiRide = false;
  paymentDetails: any;

  isMapReady: boolean = false;

  constructor(
    private corporateService: CorporateService,
    private levelOfServiceService: LevelOfServiceService,
    private serviceArea: ServiceAreasService,
    private assignTripService: AssigTripsService,
    private kpisService: KpisService,
    private driverService: DriversService,
    private sharedDataService: SharedDataService,
    private fleetService: FleetService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.trackinURL = environment.TRACKING_URL;
    if (this.trip) {
      // Instantly populate UI with data already present in memory
      this.tripData = { ...this.trip };
      if (this.tripData.priorityClient && this.tripData.priorityClient.contactNumber) {
        this.contact = this.tripData.priorityClient.contactNumber;
      }
      if (this.tripData.jobOriginLatitude && this.tripData.jobOriginLongitude) {
        this.latitude = this.tripData.jobOriginLatitude;
        this.longitude = this.tripData.jobOriginLongitude;
      }
      this.cdr.markForCheck();
    }

    // Lazy render heavy map after initial DOM paints
    setTimeout(() => {
      this.isMapReady = true;
      this.cdr.markForCheck();
    }, 150);

    if (this.trip && this.trip.attendees && this.trip.attendees.length) {
      this.getAssistByid();
    }
    this.getUser();
    if (this.trip && this.trip._id) {
      this.getTrips();
      this.getPaymentDetails();
    }
    if (this.source === "statistics") {
      this.getTripDetails();
    }
  }

  getPaymentDetails() {
    if (!this.trip || !this.trip._id) return;
    this.assignTripService
      .getTripPaymentDetails(this.trip._id)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (data) => {
          if (data && data.length) {
            this.paymentDetails = data[0];
          }
          this.cdr.markForCheck();
        },
        () => {
          this.paymentDetails = null;
          this.cdr.markForCheck();
        }
      );
  }

  getUser() {
    this.sharedDataService
      .getUser()
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(user => {
        if (user) {
          this.user = user;
          if (user.hasOwnProperty('isFarmout')) {
            this.isFarmoutIntegration = user.isFarmout;
          }
          this.cdr.markForCheck();
        }
      });
  }

  getTrips() {
    if (!this.trip || !this.trip._id) return;
    this.assignTripService
      .tripDetail(this.trip._id)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (data) => {
          if (data) {
            this.tripData = { ...this.tripData, ...data };
            if (this.tripData.driver) {
              this.getDriverDetails(this.tripData.driver);
            }

            if (this.tripData.preferredDriver) {
              this.getPreferredDriverData(this.tripData.preferredDriver);
            }

            if (data.priorityClient) {
              this.contact = data.priorityClient.contactNumber;
            }
            if (this.tripData.serviceArea) {
              this.getServiceAreas();
            }
            if (this.tripData.los) {
              this.getLevelOfServicebyId();
            }
            if (this.tripData.spaceType) {
              this.getSpaceTypesbyId();
            }
            if (this.tripData.cooperate) {
              this.getCorporateUserById();
            }
            this.cdr.markForCheck();
          }
        });
  }

  closeModal() {
    this.onCloseModal.emit();
  }

  getDriverDetails(id) {
    if (!id) return;
    this.driverService
      .getDriverProfile(id)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(data => {
        if (data) {
          this.driverDetails = data;
          this.cdr.markForCheck();
        }
      });
  }

  getETA(tripId) {
    if (!tripId) return;
    this.assignTripService
      .getETA(tripId)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(data => {
        if (data && data.etaMessage) {
          this.eta = data.etaMessage;
          this.cdr.markForCheck();
        }
      });
  }

  sendSMS() {
    if (!this.contact) return;
    this.trip.contactNumber = this.contact;

    const payload = {
      ...this.trip,
      tripId: this.trip._id,
      latitude: this.trip.driver.latitude,
      longitude: this.trip.driver.longitude,
    }
    this.assignTripService
      .sendTrackingSMS(payload)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(data => {
        if (data) {
          // this.contact = '';
          if (data.success) {
            sweetAlert('Success', data.message, 'success', 'OK');
          } else {
            sweetAlert('Error', data.message, 'error', 'OK');
          }
          this.cdr.markForCheck();
        }
      });
  }
  // sendMessage
  getTripDetails() {
    this.kpisService.getNemtStatsById(this.trip._id)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(data => {
        if (data) {
          this.trip = data;
          this.cdr.markForCheck();
        }
      })
  }

  getJobStatus(status) {
    let tripStatus = status;

    if (status === 'pending') {
      tripStatus = 'Unassigned';
    } else if (status === 'arrived') {
      tripStatus = 'On Scene';
    } else if (status === 'started') {
      tripStatus = 'Member Onboard';
    }

    return tripStatus
  }

  breakString(str) {
    if (!str) return "";
    return str.replace(/\n/g, "<br>");
  }

  getPayorTypeTitle(corporateId, payorType) {
    if (!this.corporatesByKey) return;

    let payor;
    let types = this.corporatesByKey[corporateId];
    if (types && types.length) {
      payor = types.find(t => t.value === payorType);
    }
    return payor ? payor.title : '';
  }

  getServiceAreas() {
    if (!this.tripData || !this.tripData.serviceArea) return;
    this.serviceArea.getServiceAreas({ status: 'active' })
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (data) => {
          if (data) {
            this.serviceAreas = data;
            const sArea = this.serviceAreas.find((item) => item._id === this.tripData.serviceArea);
            this.tripData.serviceArea = sArea ? sArea.name : 'N/A';
            this.cdr.markForCheck();
          }
        },
        (err) => {
          sweetAlert("Error", err, "error", "Ok");
        }
      );
  }

  getLevelOfServicebyId() {
    if (!this.tripData || !this.tripData.los) return;
    this.levelOfServiceService.getLevelOfServicebyId(this.tripData.los)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (data) => {
          if (data) {
            this.tripData.los = data.title;
            this.cdr.markForCheck();
          }
        },
        (err) => {
          sweetAlert("Error", err, "error", "Ok");
        }
      );
  }

  getSpaceTypesbyId() {
    if (!this.tripData || !this.tripData.spaceType) return;
    this.levelOfServiceService.getServiceTypebyId(this.tripData.spaceType)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (data) => {
          if (data) {
            this.tripData.spaceType = data.title;
            this.cdr.markForCheck();
          }
        },
        (err) => {
          sweetAlert("Error", err, "error", "Ok");
        }
      );
  }

  getCorporateUserById() {
    if (!this.tripData || !this.tripData.cooperate) return;
    this.corporateService.getCorporateUserById(this.tripData.cooperate)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe((data) => {
        if (data) {
          this.tripData.cooperate = data.displayName;
          if (data.companyType && data.companyType === 'taxi') {
            this.isTaxiRide = true;
          }
          this.cdr.markForCheck();
        }
      });
  }

  getAssistByid() {
    if (!this.trip || !this.trip.attendees || !this.trip.attendees.length) return;
    this.driverService
      .getAssistsByIDs(this.trip.attendees)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(data => {
        if (data) {
          this.assistList = data;
          this.cdr.markForCheck();
        }
      });
  }

  getPreferredDriverData(driverId) {
    if (!driverId) return;
    this.driverService
      .getDriversByID(driverId)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(data => {
        if (data) {
          this.preferredDriverDetails = data;
          this.cdr.markForCheck();
        }
      });
  }

  ngOnDestroy(): void {
  }
}
